/**
 * Fabric Samples Picker
 * Custom element that manages the fabric sample selection page:
 *  - Reads fabric data from an embedded JSON script
 *  - Persists selection in localStorage
 *  - Renders slot row, card selection state, and counter
 *  - Opens a detail drawer (populated dynamically) and a form drawer
 *  - Populates the hidden form input with selected fabrics on submit
 *  - Handles post-submit success: clears storage and shows the form drawer
 */
class FabricSamplePicker extends HTMLElement {
  constructor() {
    super();
    this.maxCount = parseInt(this.getAttribute('data-max') || '6', 10);
    this.storageKey = this.getAttribute('data-storage-key') || 'modulup_fabric_selection_v1';
    this.selectedHandles = [];
    this.fabricsData = {};
    this.detailDrawer = null;
    this.formDrawer = null;
    this.translations = {
      detailFor: 'Detayları gör',
      removeFor: 'Seçimi kaldır',
      addSample: 'Numuneye ekle',
      removeSample: 'Seçimden çıkar',
      colorLabel: 'Renk',
      codeLabel: 'Kod',
      lightLabel: 'Işık yoğunluğu',
    };
  }

  connectedCallback() {
    this.parseFabricData();
    this.parseTranslations();
    this.loadFromStorage();
    this.bindLocalEvents();
    this.render();

    // Drawers are siblings/later in DOM; wait for full parse before wiring them.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.lateInit(), { once: true });
    } else {
      this.lateInit();
    }
  }

  lateInit() {
    this.findDrawers();
    this.bindDrawerEvents();
    this.maybeShowSuccess();
  }

  parseFabricData() {
    const dataScript = this.querySelector('script[data-fabric-data]');
    if (!dataScript) return;
    try {
      this.fabricsData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[FabricSamplePicker] Failed to parse fabric data', e);
    }
  }

  parseTranslations() {
    const dataScript = this.querySelector('script[data-fabric-translations]');
    if (!dataScript) return;
    try {
      Object.assign(this.translations, JSON.parse(dataScript.textContent));
    } catch (e) {
      // Keep defaults
    }
  }

  findDrawers() {
    const detailId = this.getAttribute('data-detail-drawer-id');
    const formId = this.getAttribute('data-form-drawer-id');
    if (detailId) this.detailDrawer = document.getElementById(detailId);
    if (formId) this.formDrawer = document.getElementById(formId);
  }

  loadFromStorage() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      this.selectedHandles = Array.isArray(stored)
        ? stored.filter((h) => Object.prototype.hasOwnProperty.call(this.fabricsData, h))
        : [];
      if (this.selectedHandles.length > this.maxCount) {
        this.selectedHandles = this.selectedHandles.slice(0, this.maxCount);
      }
    } catch (e) {
      this.selectedHandles = [];
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.selectedHandles));
    } catch (e) {
      // localStorage unavailable; ignore
    }
  }

  clearStorage() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      // ignore
    }
    this.selectedHandles = [];
  }

  bindLocalEvents() {
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
  }

  bindDrawerEvents() {
    if (this.detailDrawer) {
      this.detailDrawer.addEventListener('click', this.onDetailDrawerClick.bind(this));
    }

    if (this.formDrawer) {
      this.formDrawer.addEventListener('drawer:afterShow', () => this.populateFormPreview());
      const form = this.formDrawer.querySelector('form');
      if (form) {
        form.addEventListener('submit', this.populateFormHiddenInput.bind(this));
      }
    }
  }

  onClick(e) {
    const detailsBtn = e.target.closest('[data-action="details"]');
    if (detailsBtn) {
      e.preventDefault();
      e.stopPropagation();
      const handle =
        detailsBtn.dataset.fabricHandle ||
        detailsBtn.closest('[data-fabric-handle]')?.dataset.fabricHandle;
      if (handle) this.openDetail(handle);
      return;
    }

    const removeBtn = e.target.closest('[data-action="slot-remove"]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const handle = removeBtn.dataset.fabricHandle;
      if (handle) this.toggle(handle, false);
      return;
    }

    const card = e.target.closest('.fabric-card[data-fabric-handle]');
    if (card) {
      const handle = card.dataset.fabricHandle;
      this.toggle(handle);
    }
  }

  onChange(e) {
    if (e.target.matches('[data-filter-category]')) {
      this.applyFilter();
    }
  }

  onDetailDrawerClick(e) {
    const toggleBtn = e.target.closest('[data-action="detail-toggle"]');
    if (!toggleBtn) return;
    const handle = toggleBtn.dataset.fabricHandle;
    if (!handle) return;
    this.toggle(handle);
    this.updateDetailToggleButton();
  }

  toggle(handle, force) {
    if (!Object.prototype.hasOwnProperty.call(this.fabricsData, handle)) return;
    const idx = this.selectedHandles.indexOf(handle);
    const isSelected = idx > -1;
    let shouldSelect;
    if (force === true) shouldSelect = true;
    else if (force === false) shouldSelect = false;
    else shouldSelect = !isSelected;

    if (shouldSelect && !isSelected) {
      if (this.selectedHandles.length >= this.maxCount) {
        this.flashLimitWarning();
        return;
      }
      this.selectedHandles.push(handle);
    } else if (!shouldSelect && isSelected) {
      this.selectedHandles.splice(idx, 1);
    } else {
      return;
    }

    this.saveToStorage();
    this.render();
  }

  render() {
    this.renderSlots();
    this.renderCards();
    this.renderCounter();
    this.populateFormPreview();
  }

  renderSlots() {
    const slots = this.querySelectorAll('[data-slot]');
    slots.forEach((slot, idx) => {
      const handle = this.selectedHandles[idx];
      if (handle) {
        const fabric = this.fabricsData[handle];
        slot.dataset.fabricHandle = handle;
        slot.classList.add('is-filled');
        slot.classList.remove('is-empty');
        slot.innerHTML = `
          <button type="button" class="fabric-slot__image" data-action="details" data-fabric-handle="${this.attr(handle)}" aria-label="${this.attr(this.translations.detailFor)}: ${this.attr(fabric.name)}">
            <img src="${this.attr(fabric.image)}" alt="${this.attr(fabric.name)}" loading="lazy" />
          </button>
          <button type="button" class="fabric-slot__remove" data-action="slot-remove" data-fabric-handle="${this.attr(handle)}" aria-label="${this.attr(this.translations.removeFor)}: ${this.attr(fabric.name)}">
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
            </svg>
          </button>
        `;
      } else {
        delete slot.dataset.fabricHandle;
        slot.classList.add('is-empty');
        slot.classList.remove('is-filled');
        slot.innerHTML = '<span class="fabric-slot__placeholder" aria-hidden="true">+</span>';
      }
    });
  }

  renderCards() {
    this.querySelectorAll('.fabric-card[data-fabric-handle]').forEach((card) => {
      const handle = card.dataset.fabricHandle;
      const isSelected = this.selectedHandles.includes(handle);
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  renderCounter() {
    const countEl = this.querySelector('[data-count]');
    if (countEl) countEl.textContent = String(this.selectedHandles.length);

    const continueBtn = this.querySelector('[data-action="continue"]');
    if (continueBtn) {
      const empty = this.selectedHandles.length === 0;
      continueBtn.disabled = empty;
      continueBtn.classList.toggle('is-disabled', empty);
    }
  }

  openDetail(handle) {
    if (!this.detailDrawer) return;
    const fabric = this.fabricsData[handle];
    if (!fabric) return;

    const contentEl = this.detailDrawer.querySelector('[data-detail-content]');
    if (contentEl) {
      const sunIcon = `
        <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4"/>
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
        </svg>
      `;
      contentEl.innerHTML = `
        <div class="fabric-detail__media">
          <img class="fabric-detail__image" data-fabric-detail-image src="${this.attr(fabric.image_large || fabric.image)}" alt="${this.attr(fabric.name)}" />
          <div class="fabric-detail__light" role="group" aria-label="${this.attr(this.translations.lightLabel)}">
            <span class="fabric-detail__light-icon fabric-detail__light-icon--low" aria-hidden="true">${sunIcon}</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value="50"
              class="fabric-detail__light-slider"
              data-fabric-light-slider
              aria-label="${this.attr(this.translations.lightLabel)}"
            />
            <span class="fabric-detail__light-icon fabric-detail__light-icon--high" aria-hidden="true">${sunIcon}</span>
          </div>
        </div>
        <div class="fabric-detail__body">
          ${fabric.category ? `<p class="fabric-detail__category">${this.escape(fabric.category)}</p>` : ''}
          <h3 class="fabric-detail__name">${this.escape(fabric.name)}</h3>
          ${fabric.color_name ? `<p class="fabric-detail__meta">${this.escape(this.translations.colorLabel)}: ${this.escape(fabric.color_name)}</p>` : ''}
          ${fabric.code ? `<p class="fabric-detail__meta">${this.escape(this.translations.codeLabel)}: ${this.escape(fabric.code)}</p>` : ''}
          ${fabric.description ? `<div class="fabric-detail__description rte">${this.escape(fabric.description).replace(/\n/g, '<br/>')}</div>` : ''}
        </div>
      `;

      this.bindLightSlider(contentEl);
    }

    const toggleBtn = this.detailDrawer.querySelector('[data-action="detail-toggle"]');
    if (toggleBtn) {
      toggleBtn.dataset.fabricHandle = handle;
    }
    this.updateDetailToggleButton();

    if (typeof this.detailDrawer.show === 'function') {
      this.detailDrawer.show();
    }
  }

  bindLightSlider(scopeEl) {
    const slider = scopeEl.querySelector('[data-fabric-light-slider]');
    const img = scopeEl.querySelector('[data-fabric-detail-image]');
    if (!slider || !img) return;

    this.applyLightFilter(img, Number(slider.value));

    let frame = null;
    const onInput = (e) => {
      const value = Number(e.target.value);
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        this.applyLightFilter(img, value);
        frame = null;
      });
    };
    slider.addEventListener('input', onInput);
  }

  applyLightFilter(img, percent) {
    const t = (Math.max(0, Math.min(100, percent)) - 50) / 50;
    const brightness = (1 + t * 0.5).toFixed(3);
    const contrast = (1 + t * 0.15).toFixed(3);
    const saturate = (1 + t * 0.25).toFixed(3);
    img.style.filter = `brightness(${brightness}) contrast(${contrast}) saturate(${saturate})`;
  }

  updateDetailToggleButton() {
    if (!this.detailDrawer) return;
    const toggleBtn = this.detailDrawer.querySelector('[data-action="detail-toggle"]');
    if (!toggleBtn) return;
    const handle = toggleBtn.dataset.fabricHandle;
    const isSelected = this.selectedHandles.includes(handle);
    const textEl = toggleBtn.querySelector('.btn-text');
    if (textEl) {
      textEl.textContent = isSelected ? this.translations.removeSample : this.translations.addSample;
    }
    toggleBtn.classList.toggle('button--secondary', isSelected);
    toggleBtn.classList.toggle('button--primary', !isSelected);
  }

  populateFormHiddenInput() {
    if (!this.formDrawer) return;
    const input = this.formDrawer.querySelector('[data-selected-fabrics-input]');
    if (!input) return;
    const lines = this.selectedHandles.map((h) => {
      const f = this.fabricsData[h];
      const code = f.code ? ` (${f.code})` : '';
      return `${f.name}${code}`;
    });
    input.value = lines.join(' | ');
  }

  populateFormPreview() {
    if (!this.formDrawer) return;
    const previewEl = this.formDrawer.querySelector('[data-form-preview]');
    if (!previewEl) return;
    if (this.selectedHandles.length === 0) {
      previewEl.innerHTML = '<li class="fabric-preview__item" style="opacity:.6">Henüz numune seçmediniz.</li>';
      return;
    }
    previewEl.innerHTML = this.selectedHandles
      .map((h) => {
        const f = this.fabricsData[h];
        return `
          <li class="fabric-preview__item">
            <img src="${this.attr(f.image)}" alt="${this.attr(f.name)}" />
            <span class="fabric-preview__name">${this.escape(f.name)}</span>
          </li>
        `;
      })
      .join('');
    // Also pre-fill the hidden input so it's there even before submit
    this.populateFormHiddenInput();
  }

  flashLimitWarning() {
    const warning = this.querySelector('[data-limit-warning]');
    if (!warning) return;
    warning.classList.add('is-visible');
    clearTimeout(this._warningTimer);
    this._warningTimer = setTimeout(() => warning.classList.remove('is-visible'), 2200);
  }

  applyFilter() {
    const checked = Array.from(this.querySelectorAll('[data-filter-category]:checked')).map(
      (input) => input.value,
    );
    const showAll = checked.length === 0;
    this.querySelectorAll('.fabric-card[data-category]').forEach((card) => {
      const cat = card.dataset.category;
      const visible = showAll || checked.includes(cat);
      card.hidden = !visible;
    });
  }

  maybeShowSuccess() {
    if (!this.formDrawer) return;
    const successEl = this.formDrawer.querySelector('[data-fabric-form-success]');
    if (!successEl) return;
    this.clearStorage();
    this.render();
    if (typeof this.formDrawer.show === 'function') {
      requestAnimationFrame(() => this.formDrawer.show());
    }
  }

  escape(str) {
    if (str == null) return '';
    return String(str).replace(/[<>"'&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '&': '&amp;' }[c]));
  }

  attr(str) {
    return this.escape(str);
  }
}

customElements.define('fabric-sample-picker', FabricSamplePicker);
